import {
  type CamelCased,
  type CampaignMetrics,
  CampaignOverviewFilterTargetEnum,
  type CampaignSourceMetrics,
  type CampaignStats,
  type FinanceChartMetric,
  type FinanceChartResponse,
  type FinanceMetricServer,
} from '@metapic-apps/shared'

import { normalizeMoney } from '@/lib/adapters/common.adapter'
import type { MoneyServerType } from '@/lib/types/common.types'

const CURRENT_SERIES_KEY = 'current'

const METRIC_FIELD: Record<FinanceChartMetric, keyof Omit<CampaignMetrics, 'linksCreated'>> = {
  clicks: 'clicks',
  number_of_orders: 'orders',
  order_value: 'orderValue',
  revenue_potential: 'estimatedCost',
}

export const FINANCE_CAMPAIGN_METRICS = Object.keys(METRIC_FIELD) as FinanceChartMetric[]

const TARGET_METRIC: Partial<Record<CampaignOverviewFilterTargetEnum, FinanceChartMetric>> = {
  [CampaignOverviewFilterTargetEnum.Clicks]: 'clicks',
  [CampaignOverviewFilterTargetEnum.Orders]: 'number_of_orders',
  [CampaignOverviewFilterTargetEnum.OrderValue]: 'order_value',
  [CampaignOverviewFilterTargetEnum.TotalCost]: 'revenue_potential',
}

export const getFinanceChartMetric = (
  target: CampaignOverviewFilterTargetEnum,
): FinanceChartMetric | undefined => TARGET_METRIC[target]

const SOURCE_CODE: Record<string, CampaignSourceMetrics['source']> = {
  OTHER: 0,
  INSTAGRAM: 1,
  TIKTOK: 2,
  SNAPCHAT: 3,
}

const toNumericValue = (value: number | MoneyServerType): number =>
  typeof value === 'number' ? value : normalizeMoney(value).amount

// The advertiser overview chart groups the daily finance datapoints into fixed-size
// spans (3-day buckets) so the series stays readable over long date ranges.
const CHART_BUCKET_SIZE = 3

const chunk = <T>(items: readonly T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

const emptyMetrics = (): CampaignMetrics => ({
  linksCreated: 0,
  clicks: 0,
  orders: 0,
  orderValue: 0,
  estimatedCost: 0,
  estimatedEarnings: 0,
  estimatedGp: 0,
  estimatedGpRatio: 0,
})

export const normalizeFinanceCampaignSummary = (
  metrics: CamelCased<FinanceMetricServer>[],
): CampaignMetrics =>
  metrics.reduce<CampaignMetrics>((summary, metric) => {
    const field = METRIC_FIELD[metric.key as FinanceChartMetric]
    return field ? { ...summary, [field]: toNumericValue(metric.value) } : summary
  }, emptyMetrics())

export const normalizeFinanceCampaignDailyStats = (
  response: CamelCased<FinanceChartResponse>,
  metric: FinanceChartMetric,
): CampaignStats['dailyStats'] => {
  const field = METRIC_FIELD[metric]
  const series = response.series ?? []
  const current = series.find((item) => item.key === CURRENT_SERIES_KEY) ?? series[0]
  const { linksCreated: _linksCreated, ...sourceBase } = emptyMetrics()

  return chunk(current?.data ?? [], CHART_BUCKET_SIZE).map((bucket) => {
    const bucketValue = bucket.reduce((sum, point) => sum + toNumericValue(point.value), 0)

    // Sum each traffic source's contribution across the days in the bucket,
    // keeping first-seen order so the breakdown stays stable.
    const sourceTotals = bucket.reduce<Map<string, number>>((totals, point) => {
      ;(point.breakdown ?? []).forEach((entry) => {
        totals.set(entry.key, (totals.get(entry.key) ?? 0) + toNumericValue(entry.value))
      })
      return totals
    }, new Map())

    return {
      ...emptyMetrics(),
      [field]: bucketValue,
      // The bucket is labelled by its first day (the start of the 2-day span).
      startDate: bucket[0].date,
      sources: [...sourceTotals].flatMap<CampaignSourceMetrics>(([key, value]) => {
        const source = SOURCE_CODE[key]
        return source === undefined ? [] : [{ ...sourceBase, source, [field]: value }]
      }),
    }
  })
}
